import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccountBalance as AccountBalanceIcon,
  ShoppingCart as ShoppingCartIcon,
  Home as HomeIcon,
  LocalHospital as HealthIcon,
  School as EducationIcon,
  DirectionsCar as TransportationIcon,
  Restaurant as FoodIcon,
  MoreHoriz as OtherIcon,
  CheckCircle as CheckCircleIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowDropDown as ExpandMoreIcon,
  ArrowDropUp as ExpandLessIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Close as XMarkIcon
} from '@mui/icons-material';
import {
  Wifi as WifiIcon,
  PhoneIphone as PhoneIcon,
  Pets as PetsIcon,
  ChildCare as ChildCareIcon,
  Pool as PoolIcon,
  VerifiedUser as ShieldIcon
} from '@mui/icons-material';
import { ViewModule as ViewModuleIcon, Tune as TuneIcon } from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { createExpense, updateExpense, deleteExpense, recordExpensePayment } from '../services/cashFlowService';
import expenseService from '../../../services/expenseService';
import { Suspense } from 'react';
const ExpenseStatsDashboard = React.lazy(() => import('./ExpenseStatsDashboard'));
import incomeService from '../../income/services/incomeService';
import moneyBucketService from '../../income/services/moneyBucketService';

// Define category icons with colors
const CATEGORY_CONFIG = {
  'Housing': { icon: HomeIcon, color: '#4F46E5', bgColor: '#EEF2FF' },
  'Utilities': { icon: AccountBalanceIcon, color: '#0891B2', bgColor: '#ECFEFF' },
  'Food': { icon: FoodIcon, color: '#16A34A', bgColor: '#ECFDF5' },
  'Transportation': { icon: TransportationIcon, color: '#EA580C', bgColor: '#FFF7ED' },
  'Healthcare': { icon: HealthIcon, color: '#DC2626', bgColor: '#FEF2F2' },
  'Insurance': { icon: HealthIcon, color: '#7C3AED', bgColor: '#F5F3FF' },
  'Education': { icon: EducationIcon, color: '#2563EB', bgColor: '#EFF6FF' },
  'Entertainment': { icon: ShoppingCartIcon, color: '#D97706', bgColor: '#FFFBEB' },
  'Other': { icon: OtherIcon, color: '#6B7280', bgColor: '#F9FAFB' }
};

// Small, colorful toggle card for selections (supports emoji)
const ToggleCard = ({ selected, onClick, label, emoji, color = 'indigo' }) => {
  const selectedMap = {
    indigo: 'bg-indigo-50 border-indigo-300 text-indigo-700',
    sky: 'bg-sky-50 border-sky-300 text-sky-700',
    rose: 'bg-rose-50 border-rose-300 text-rose-700',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-700',
    amber: 'bg-amber-50 border-amber-300 text-amber-800'
  };
  const selectedClasses = selectedMap[color] || selectedMap.indigo;
  return (
    <button
      type="button"
      aria-pressed={!!selected}
      onClick={onClick}
      className={`px-3 py-2 rounded-md border text-sm flex items-center justify-center transition select-none
        ${selected ? `${selectedClasses} shadow-sm` : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}
    >
      {emoji ? <span className="mr-1 text-base" aria-hidden> {emoji} </span> : null}
      <span>{label}</span>
    </button>
  );
};

// Feature flag for Guided Expense wizard (set VITE_FEATURE_GUIDED_EXPENSE=true to enable)
// Also enable automatically on known staging hosts as a safe fallback
const resolveGuidedFlag = () => {
  try {
    const envEnabled = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_FEATURE_GUIDED_EXPENSE === 'true';
    if (envEnabled) return true;
    if (typeof window !== 'undefined') {
      const host = window.location.hostname || '';
      const isStagingHost = /staging\.|-staging\.|\bstaging\b/.test(host) || host.includes('staging.thefynz.com');
      return isStagingHost;
    }
  } catch (_) {
    // no-op
  }
  return false;
};

const GUIDED_EXPENSE_ENABLED = resolveGuidedFlag();

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
  { value: 'one-time', label: 'One-time' }
];

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

// Add ordinal formatting function near the top of the file
const formatOrdinal = (num) => {
  if (!num) return '';
  const number = parseInt(num);
  const suffix = ['th', 'st', 'nd', 'rd'][number % 100 > 10 && number % 100 < 20 ? 0 : number % 10] || 'th';
  return `${number}${suffix}`;
};

// SortableExpenseCard component
const SortableExpenseCard = ({ expense, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showActionMenu, setShowActionMenu] = useState(false);
  
  const categoryConfig = CATEGORY_CONFIG[expense.category] || CATEGORY_CONFIG['Other'];
  const CategoryIcon = categoryConfig.icon;
  
  // Check if expense is due based on dueDate or dayOfMonth
  const isDue = expense.dueDate 
    ? new Date(expense.dueDate) <= new Date()
    : expense.dayOfMonth 
      ? new Date().getDate() >= expense.dayOfMonth 
      : false;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: expense._id,
    data: {
      type: 'expense',
      expense
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.5 : 1
  };

  // Initialize edit form data
  const initializeEditForm = () => {
    setEditFormData({
      name: expense.name || '',
      amount: expense.amount || '',
      type: expense.type || 'fixed',
      category: expense.category || '',
      frequency: expense.frequency || 'monthly',
      dayOfMonth: expense.dayOfMonth || '',
      dueDate: expense.dueDate ? expense.dueDate.split('T')[0] : '',
      vendor: expense.vendor || '',
      state: expense.state || '',
      isRecurring: expense.isRecurring !== undefined ? expense.isRecurring : true,
      notes: expense.notes || '',
      tags: expense.tags || [],
      status: expense.status || 'active',
      
      // Tax information (if applicable)
      isTaxDeductible: expense.isTaxDeductible || false,
      taxCategory: expense.taxCategory || '',
      
      // Payment method
      paymentMethod: expense.paymentMethod || 'auto',
      accountNumber: expense.accountNumber || ''
    });
  };

  // Handle edit form field changes
  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate annual amount if frequency changes
    if (field === 'frequency' || field === 'amount') {
      const amount = parseFloat(field === 'amount' ? value : editFormData.amount) || 0;
      const frequency = field === 'frequency' ? value : editFormData.frequency;
      
      // You could add automatic frequency-based calculations here
    }
  };

  const handleEditClick = () => {
    initializeEditForm();
    setIsEditMode(true);
    setExpanded(true); // Auto-expand when editing
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
    setEditFormData({});
  };

  const handleEditSave = async () => {
    try {
      setIsSaving(true);
      
      // Prepare update data
      const updateData = {
        name: editFormData.name,
        amount: parseFloat(editFormData.amount) || 0,
        type: editFormData.type,
        category: editFormData.category,
        frequency: editFormData.frequency.toLowerCase(),
        dayOfMonth: editFormData.dayOfMonth ? parseInt(editFormData.dayOfMonth) : null,
        dueDate: editFormData.dueDate || null,
        vendor: editFormData.vendor,
        state: editFormData.state,
        isRecurring: editFormData.isRecurring,
        notes: editFormData.notes,
        tags: editFormData.tags,
        status: editFormData.status,
        isTaxDeductible: editFormData.isTaxDeductible,
        taxCategory: editFormData.taxCategory,
        paymentMethod: editFormData.paymentMethod,
        accountNumber: editFormData.accountNumber
      };

      // Call the onEdit function (this should be updated to handle the edit properly)
      await onEdit(expense, updateData);
      
      setIsEditMode(false);
      setEditFormData({});
    } catch (error) {
      console.error('Error saving expense:', error);
      // Handle error (show notification, etc.)
    } finally {
      setIsSaving(false);
    }
  };
  
  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className="relative bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 mb-3"
    >
      {/* Header - Similar to income cards */}
      <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div 
              {...attributes} 
              {...listeners}
              className="cursor-grab hover:cursor-grabbing p-1 rounded hover:bg-gray-100 flex-shrink-0"
            >
              <DragIndicatorIcon sx={{ fontSize: 16 }} className="text-gray-400" />
            </div>
            {/* Removed guidedError display here; out of scope for SortableExpenseCard */}
            <div 
              className="p-2 rounded-lg flex-shrink-0"
              style={{ backgroundColor: categoryConfig.bgColor }}
            >
              <CategoryIcon 
                sx={{ fontSize: 20 }}
                style={{ color: categoryConfig.color }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">{expense.name}</h3>
            </div>
          </div>
          
          {/* Action Menu Button - Made smaller */}
          <div className="relative">
            {isEditMode ? (
              <div className="flex items-center space-x-1">
                <button 
                  onClick={handleEditSave}
                  disabled={isSaving}
                  className="p-1.5 rounded-full text-green-600 hover:text-green-800 hover:bg-green-100 disabled:opacity-50"
                  title="Save changes"
                >
                  <CheckIcon sx={{ fontSize: 16 }} />
                </button>
                <button
                  onClick={handleEditCancel}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  title="Cancel editing"
                >
                  <XMarkIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowActionMenu(!showActionMenu)}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                title="Actions"
              >
                <OtherIcon sx={{ fontSize: 16 }} />
              </button>
            )}
            
            {/* Action Menu Dropdown */}
            {showActionMenu && !isEditMode && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[120px]">
                <button
                  onClick={() => {
                    handleEditClick();
                    setShowActionMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <EditIcon sx={{ fontSize: 16 }} className="mr-2" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onDelete(expense._id);
                    setShowActionMenu(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                >
                  <DeleteIcon sx={{ fontSize: 16 }} className="mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content - Improved mobile layout */}
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Payment Information - Mobile Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Amount</div>
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              {formatCurrency(expense.amount)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {expense.frequency}
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Category</div>
            <div className="text-base sm:text-lg font-semibold text-gray-900">
              {expense.category}
            </div>
            {expense.vendor && (
              <div className="text-xs text-gray-500 mt-1 truncate">
                {expense.vendor}
              </div>
            )}
          </div>
          {/* Show day of month instead of due date, or both */}
          {(expense.dayOfMonth || expense.dueDate) && (
            <div className="bg-blue-50 p-3 rounded-lg sm:col-span-2">
              {expense.dayOfMonth ? (
                <>
                  <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Due Day</div>
                  <div className="text-base sm:text-lg font-semibold text-blue-600">
                    {formatOrdinal(expense.dayOfMonth)} of each month
                  </div>
                </>
              ) : expense.dueDate ? (
                <>
                  <div className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Due Date</div>
                  <div className="text-base sm:text-lg font-semibold text-blue-600">
                    {formatDate(expense.dueDate)}
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>

        {/* Expand/Collapse Toggle */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
          <button
            onClick={toggleExpand}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <span>{expanded ? 'Hide Details' : 'Show Details'}</span>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            )}
          </button>
        </div>
        
        {/* Expanded details */}
        {expanded && (
          <div className="pt-4 border-t border-gray-200">
            {isEditMode ? (
              <div className="space-y-4">
                {/* Basic Information Section */}
              <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Expense Name</label>
                      <input
                        type="text"
                        value={editFormData.name || ''}
                        onChange={(e) => handleEditFieldChange('name', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Expense name"
                      />
              </div>
              <div>
                      <label className="block text-xs text-gray-600 mb-1">Vendor/Company</label>
                      <input
                        type="text"
                        value={editFormData.vendor || ''}
                        onChange={(e) => handleEditFieldChange('vendor', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Vendor name"
                      />
              </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Category</label>
                      <select
                        value={editFormData.category || ''}
                        onChange={(e) => handleEditFieldChange('category', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Category</option>
                        {Object.keys(CATEGORY_CONFIG).map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Type</label>
                      <select
                        value={editFormData.type || ''}
                        onChange={(e) => handleEditFieldChange('type', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="fixed">Fixed</option>
                        <option value="variable">Variable</option>
                      </select>
                      </div>
                  </div>
                </div>

                {/* Payment Details Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Payment Details</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Amount</label>
                      <input
                        type="number"
                        value={editFormData.amount || ''}
                        onChange={(e) => handleEditFieldChange('amount', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Frequency</label>
                      <select
                        value={editFormData.frequency || ''}
                        onChange={(e) => handleEditFieldChange('frequency', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {FREQUENCIES.map(freq => (
                          <option key={freq.value} value={freq.value}>{freq.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={editFormData.dueDate || ''}
                        onChange={(e) => handleEditFieldChange('dueDate', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Day of Month</label>
                      <select
                        value={editFormData.dayOfMonth || ''}
                        onChange={(e) => handleEditFieldChange('dayOfMonth', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select day</option>
                        {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                          <option key={day} value={day}>{formatOrdinal(day)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Location & Tax Information */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Location & Tax Information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">State</label>
                      <select
                        value={editFormData.state || ''}
                        onChange={(e) => handleEditFieldChange('state', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select State</option>
                        {US_STATES.map(state => (
                          <option key={state.value} value={state.value}>{state.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Tax Category</label>
                      <select
                        value={editFormData.taxCategory || ''}
                        onChange={(e) => handleEditFieldChange('taxCategory', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Not Tax Deductible</option>
                        <option value="business">Business Expense</option>
                        <option value="medical">Medical Expense</option>
                        <option value="charitable">Charitable Donation</option>
                        <option value="education">Education Expense</option>
                        <option value="other">Other Deductible</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editFormData.isTaxDeductible || false}
                        onChange={(e) => handleEditFieldChange('isTaxDeductible', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-xs text-gray-600">Tax Deductible</span>
                    </label>
                  </div>
                </div>

                {/* Payment Settings */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Payment Settings</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Payment Method</label>
                      <select
                        value={editFormData.paymentMethod || ''}
                        onChange={(e) => handleEditFieldChange('paymentMethod', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="auto">Auto Pay</option>
                        <option value="manual">Manual Payment</option>
                        <option value="check">Check</option>
                        <option value="cash">Cash</option>
                        <option value="credit">Credit Card</option>
                        <option value="debit">Debit Card</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Status</label>
                      <select
                        value={editFormData.status || ''}
                        onChange={(e) => handleEditFieldChange('status', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editFormData.isRecurring !== undefined ? editFormData.isRecurring : true}
                        onChange={(e) => handleEditFieldChange('isRecurring', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-xs text-gray-600">Recurring Expense</span>
                    </label>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Notes</label>
                  <textarea
                    value={editFormData.notes || ''}
                    onChange={(e) => handleEditFieldChange('notes', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional notes or details"
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {expense.vendor && (
                  <div>
                    <span className="text-xs text-gray-600">Vendor: </span>
                    <span className="text-sm text-gray-900">{expense.vendor}</span>
                      </div>
                    )}
                {expense.dueDate && (
                  <div>
                    <span className="text-xs text-gray-600">Due Date: </span>
                    <span className="text-sm text-gray-900">{formatDate(expense.dueDate)}</span>
                  </div>
                )}
                {expense.notes && (
                  <div>
                    <span className="text-xs text-gray-600">Notes: </span>
                    <span className="text-sm text-gray-900">{expense.notes}</span>
                </div>
              )}
                {expense.isTaxDeductible && (
                  <div className="flex items-center">
                    <CheckCircleIcon fontSize="small" className="text-green-500 mr-1" />
                    <span className="text-xs text-green-600">Tax Deductible</span>
            </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main ExpenseSection component
const ExpenseSection = ({ expenses, onExpensesChange, isPartnerMode, isOurMode, partnerName, netMonthly: propNetMonthly }) => {
  // Always treat expenses as an array
  const safeExpenses = Array.isArray(expenses) ? expenses : [];

  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openGuidedWizard, setOpenGuidedWizard] = useState(false);
  // View mode for the expenses list
  const [viewMode, setViewMode] = useState('sliders'); // default to sliders view
  const [editedAmounts, setEditedAmounts] = useState({}); // id -> amount
  const [savingAll, setSavingAll] = useState(false);
  const [sliderBaselines, setSliderBaselines] = useState({}); // id -> initial amount snapshot
  const [sortMode, setSortMode] = useState('none');
  const modalAnchorRef = useRef(120);
  const payModalAnchorRef = useRef(120);
  const [guidedStep, setGuidedStep] = useState(0); // 0: Income, 1: Household, 2: Fixed, 3: Variable, 4: Review
  const TOTAL_WIZARD_STEPS = 5;
  const [guidedLoading, setGuidedLoading] = useState(false);
  const [guidedError, setGuidedError] = useState('');
  const [incomeForm, setIncomeForm] = useState({
    name: 'Primary Income',
    amount: '',
    frequency: 'monthly'
  });
  const [existingIncomes, setExistingIncomes] = useState([]);
  const [selectedIncomeIds, setSelectedIncomeIds] = useState([]);
  const [addIncomeOpen, setAddIncomeOpen] = useState(false);
  const [newIncomeForm, setNewIncomeForm] = useState({ name: '', gross: '', frequency: 'monthly', state: '' });
  const [newIncomeEstNet, setNewIncomeEstNet] = useState('');
  const [netMonthly, setNetMonthly] = useState(0);
  
  // Helpers
  const toMonthly = (amount, frequency) => {
    const n = parseFloat(amount) || 0;
    const f = (frequency || 'monthly').toLowerCase();
    const map = { monthly: 1, biweekly: 26/12, weekly: 52/12, yearly: 1/12, onetime: 0 };
    return n * (map[f] ?? 1);
  };
  const isActiveIncome = (inc) => {
    if (inc?.isActive === false) return false;
    const now = new Date();
    const startOk = !inc?.startDate || new Date(inc.startDate) <= now;
    const endOk = !inc?.endDate || new Date(inc.endDate) >= now;
    return startOk && endOk;
  };

  // Use netMonthly from props if provided (for "Our Cash Flow" mode), otherwise calculate from income
  useEffect(() => {
    // If netMonthly is provided as prop (from parent's summary), use it
    // This ensures "Our Cash Flow" mode uses combined income
    if (propNetMonthly !== undefined && propNetMonthly !== null) {
      console.log('💰 ExpenseSection: Using netMonthly from props:', propNetMonthly, { isOurMode, isPartnerMode });
      setNetMonthly(propNetMonthly);
      return;
    }
    
    // Otherwise, compute from user's own income (fallback for "My Cash Flow" mode)
    const computeNet = async () => {
      try {
        const res = await incomeService.getAll();
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        const total = list.reduce((sum, inc) => {
          const base = (inc?.netAmount != null ? inc.netAmount : inc?.amount) || 0;
          return sum + toMonthly(base, inc?.frequency);
        }, 0);
        console.log('💰 ExpenseSection: Calculated netMonthly from income:', total);
        setNetMonthly(total);
      } catch {
        setNetMonthly(0);
      }
    };
    computeNet();
  }, [propNetMonthly, isOurMode, isPartnerMode]);

  const CATEGORY_RANGES = {
    Housing: [20, 35], Utilities: [3, 8], Food: [10, 20], Transportation: [10, 15], Insurance: [3, 10], Healthcare: [2, 8], Entertainment: [1, 4], Other: [0, 3]
  };

  const getBandFor = (category, pct) => {
    const [low, high] = CATEGORY_RANGES[category] || [1, 10];
    if (pct < low) return { label: 'Low', color: 'text-teal-600' };
    if (pct > high) return { label: 'High', color: 'text-rose-600' };
    return { label: 'Just Right', color: 'text-emerald-600' };
  };

  // Capture stable baselines when entering sliders view or expenses change, to prevent runaway slider max
  useEffect(() => {
    if (viewMode !== 'sliders') return;
    const filtered = safeExpenses.filter(exp => {
      if (activeTab === 0) return exp.type === 'fixed';
      if (activeTab === 1) return exp.type === 'variable';
      return true;
    });
    const next = {};
    filtered.forEach(exp => { next[exp._id] = exp.amount || 0; });
    setSliderBaselines(next);
  }, [viewMode, safeExpenses, activeTab]);

  // Load existing incomes when wizard opens
  useEffect(() => {
    const load = async () => {
      try {
        const res = await incomeService.getAll();
        const list = Array.isArray(res?.data) ? res.data.filter(isActiveIncome) : [];
        const enriched = await Promise.all(list.map(async (inc) => {
          const grossMonthly = toMonthly(inc.amount, inc.frequency);
          let netMonthly = inc.netAmount ? toMonthly(inc.netAmount, inc.frequency) : null;
          if (netMonthly == null && inc.amount) {
            try {
              const est = await incomeService.estimateNet({ basePerPeriod: inc.amount, frequency: inc.frequency });
              const netPerPeriod = est?.netPerPeriod || 0;
              netMonthly = toMonthly(netPerPeriod, inc.frequency);
            } catch {
              netMonthly = null;
            }
          }
          return { ...inc, __grossMonthly: grossMonthly, __netMonthly: netMonthly };
        }));
        setExistingIncomes(enriched);
      } catch {
        setExistingIncomes([]);
      }
    };
    if (openGuidedWizard) load();
  }, [openGuidedWizard]);

  // Auto-estimate net for Add Income modal
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const gross = parseFloat(newIncomeForm.gross);
      if (!gross || Number.isNaN(gross)) { if (!cancelled) setNewIncomeEstNet(''); return; }
      try {
        const est = await incomeService.estimateNet({ basePerPeriod: gross, frequency: newIncomeForm.frequency, taxProfile: { state: newIncomeForm.state || undefined } });
        if (!cancelled) setNewIncomeEstNet(est?.netPerPeriod ? String(est.netPerPeriod) : '');
      } catch {
        if (!cancelled) setNewIncomeEstNet('');
      }
    };
    run();
    return () => { cancelled = true; };
  }, [newIncomeForm.gross, newIncomeForm.frequency, newIncomeForm.state]);

  // Step 3: Fixed expenses
  const [fixed, setFixed] = useState({
    hasElectric: false, electricMonthly: '',
    hasGas: false, gasMonthly: '',
    hasWater: false, waterMonthly: '',
    hasInternet: false, internetMonthly: '',
    hasMobile: false, mobileMonthly: '',
    hasHealthInsurance: false, healthInsuranceMonthly: '',
    healthOOPMonthly: '',
    hasAutoInsurance: false, autoInsuranceMonthly: '',
    hasSubscriptions: false, subscriptionsMonthly: ''
  });

  // Step 4: Variable expenses + vehicles
  const [variable, setVariable] = useState({
    hasGroceries: false, groceriesMonthly: '',
    hasDining: false, diningMonthly: '',
    hasFuel: false, fuelMonthly: '',
    hasMaintenance: false, maintenanceMonthly: '',
    hasChildcare: false, childcareMonthly: '',
    hasPets: false, petFoodMonthly: '', vetMonthly: '', petInsuranceMonthly: '',
    vehicles: [] // { ownership:'own'|'lease'|'finance', year:'', make:'', model:'', payment:'' }
  });

  // Step 5: Review items
  const [reviewItems, setReviewItems] = useState([]); // [{key,name,category,amount,percent}]
  const [reviewAdjustments, setReviewAdjustments] = useState({}); // key -> amount

  // When wizard is open, prevent background/body scroll for true modal behavior
  useEffect(() => {
    if (!GUIDED_EXPENSE_ENABLED) return;
    if (openGuidedWizard) {
      try {
        document.documentElement.classList.add('overflow-hidden');
        document.body.classList.add('overflow-hidden');
      } catch (_) {}
    } else {
      try {
        document.documentElement.classList.remove('overflow-hidden');
        document.body.classList.remove('overflow-hidden');
      } catch (_) {}
    }
    return () => {
      try {
        document.documentElement.classList.remove('overflow-hidden');
        document.body.classList.remove('overflow-hidden');
      } catch (_) {}
    };
  }, [openGuidedWizard]);
  const [household, setHousehold] = useState({
    housing: '', // own | rent | neither
    hasRentersInsurance: false,
    hoa: false,
    propertyTaxInMortgage: true,
    householdSize: 1,
    state: '',
    zip: '',
    squareFootage: '',
    purchasePrice: '',
    marketPrice: '',
    // Amount entries (monthly)
    rentAmount: '',
    mortgageAmount: '',
    propertyTaxMonthly: '',
    hoaMonthly: '',
    homeInsuranceMonthly: '',
    rentersInsuranceMonthly: ''
  });
  // vehicles and other categories removed from Step 2
  
  // Toggle helpers
  const toggleHousing = (value) => {
    setHousehold(prev => ({ ...prev, housing: prev.housing === value ? '' : value }));
  };

  // Clear fields when housing type is deselected or switched
  useEffect(() => {
    setHousehold(prev => {
      const next = { ...prev };
      if (prev.housing !== 'own') {
        next.mortgageAmount = '';
        next.homeInsuranceMonthly = '';
        next.propertyTaxMonthly = '';
        next.hoaMonthly = '';
        next.hoa = false;
        next.propertyTaxInMortgage = true;
      }
      if (prev.housing !== 'rent') {
        next.rentAmount = '';
        next.hasRentersInsurance = false;
        next.rentersInsuranceMonthly = '';
      }
      return next;
    });
  }, [household.housing]);

  // Step 2 validation (Household profile only)
  const isStep2Valid = useMemo(() => {
    if (!household.housing) return false;
    const num = (v) => (v !== '' && v !== null && v !== undefined && !Number.isNaN(parseFloat(v)) && parseFloat(v) > 0);
    if (household.housing === 'rent') {
      if (!num(household.rentAmount)) return false;
      if (household.hasRentersInsurance && !num(household.rentersInsuranceMonthly)) return false;
    }
    if (household.housing === 'own') {
      if (!num(household.mortgageAmount)) return false;
      if (!num(household.homeInsuranceMonthly)) return false;
      if (!household.propertyTaxInMortgage && !num(household.propertyTaxMonthly)) return false;
      if (household.hoa && !num(household.hoaMonthly)) return false;
    }
    return true;
  }, [household]);
  const [proposals, setProposals] = useState([]);
  const [selectedProposals, setSelectedProposals] = useState({});
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    type: 'fixed',
    category: '',
    frequency: 'monthly',
    dayOfMonth: '',
    isRecurring: true,
    notes: '',
    tags: [],
    status: 'active'
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    status: 'completed',
    notes: ''
  });

  // Grouped expenses by category for better organization
  const groupedExpenses = useMemo(() => {
    const filtered = safeExpenses.filter(expense => {
      if (activeTab === 0) return expense.type === 'fixed';
      if (activeTab === 1) return expense.type === 'variable';
      return true;
    });
    
    // Group by category
    const groups = {};
    filtered.forEach(expense => {
      const category = expense.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(expense);
    });
    
    return groups;
  }, [safeExpenses, activeTab]);

  // Setup sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Flat list of all filtered expenses for drag and drop
  const filteredExpenses = useMemo(() => {
    return safeExpenses.filter(expense => {
      if (activeTab === 0) return expense.type === 'fixed';
      if (activeTab === 1) return expense.type === 'variable';
      return true;
    });
  }, [safeExpenses, activeTab]);

  // Calculate stats for expense dashboard
  const expenseStats = useMemo(() => {
    // Total monthly expenses
    const totalMonthly = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    // Count overdue expenses
    const overdueCount = filteredExpenses.filter(
      expense => expense.dueDate && new Date(expense.dueDate) <= new Date()
    ).length;
    
    // Average expense amount
    const averageAmount = filteredExpenses.length > 0 
      ? totalMonthly / filteredExpenses.length 
      : 0;
    
    // Fixed vs Variable ratio
    const fixedExpenses = safeExpenses.filter(expense => expense.type === 'fixed');
    const fixedTotal = fixedExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    const fixedRatio = totalMonthly > 0 ? (fixedTotal / totalMonthly) * 100 : 0;
    
    // Top spending category
    const categoryTotals = {};
    filteredExpenses.forEach(expense => {
      const category = expense.category || 'Other';
      if (!categoryTotals[category]) {
        categoryTotals[category] = 0;
      }
      categoryTotals[category] += expense.amount;
    });
    
    let topCategory = { name: 'None', amount: 0 };
    Object.entries(categoryTotals).forEach(([category, amount]) => {
      if (amount > topCategory.amount) {
        topCategory = { name: category, amount };
      }
    });
    
    return {
      totalMonthly,
      overdueCount,
      averageAmount,
      fixedRatio,
      topCategory
    };
  }, [filteredExpenses, safeExpenses]);

  // Overview totals: Monthly Net, Fixed, Variable, Remaining After Fixed, Remaining After All
  const overview = useMemo(() => {
    const fixed = safeExpenses.filter(e => e.type === 'fixed').reduce((s, e) => s + (e.amount || 0), 0);
    const variable = safeExpenses.filter(e => e.type === 'variable').reduce((s, e) => s + (e.amount || 0), 0);
    const remainingAfterFixed = Math.max(0, (netMonthly || 0) - fixed);
    const remainingAfterAll = Math.max(0, (netMonthly || 0) - fixed - variable);
    const clamp = (n) => (isFinite(n) && n >= 0 ? n : 0);
    return {
      net: clamp(netMonthly || 0),
      fixed: clamp(fixed),
      variable: clamp(variable),
      remainingAfterFixed: clamp(remainingAfterFixed),
      remainingAfterAll: clamp(remainingAfterAll)
    };
  }, [safeExpenses, netMonthly]);

  // Fetch expenses if they're not provided
  useEffect(() => {
    if (!safeExpenses || safeExpenses.length === 0) {
      const fetchExpenses = async () => {
        try {
          const expensesData = await expenseService.getAllExpenses();
          onExpensesChange(expensesData);
        } catch (error) {
          console.error('Error fetching expenses:', error);
        }
      };
      
      fetchExpenses();
    }
  }, [safeExpenses, onExpensesChange]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) {
      return;
    }

    const oldIndex = filteredExpenses.findIndex(exp => exp._id === active.id);
    const newIndex = filteredExpenses.findIndex(exp => exp._id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(filteredExpenses, oldIndex, newIndex);
      // Here you would typically update the order in your database
      // For now, we'll just update the local state
      onExpensesChange([...reordered]);
    }
  };

  const handleOpenDialog = (expense = null, category = '', evt = null) => {
    const scrollY = typeof window !== 'undefined' ? (window.scrollY || document.documentElement.scrollTop || 0) : 0;
    const clientY = evt?.clientY ?? (typeof window !== 'undefined' ? window.innerHeight / 2 : 200);
    modalAnchorRef.current = Math.max(80, scrollY + clientY - 150);
    if (expense) {
      setFormData({
        ...expense,
        dayOfMonth: expense.dayOfMonth || ''
      });
      setSelectedExpense(expense);
    } else {
      setFormData({
        name: '',
        amount: '',
        type: activeTab === 0 ? 'fixed' : 'variable',
        category: category || '',
        frequency: 'monthly',
        dayOfMonth: '',
        isRecurring: true,
        notes: '',
        tags: [],
        status: 'active'
      });
      setSelectedExpense(null);
      setSelectedCategory(category);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedExpense(null);
    setSelectedCategory('');
  };

  const handleOpenPaymentDialog = (expense, evt = null) => {
    const scrollY = typeof window !== 'undefined' ? (window.scrollY || document.documentElement.scrollTop || 0) : 0;
    const clientY = evt?.clientY ?? (typeof window !== 'undefined' ? window.innerHeight / 2 : 200);
    payModalAnchorRef.current = Math.max(80, scrollY + clientY - 150);
    setSelectedExpense(expense);
    setPaymentData({
      amount: expense.amount,
      date: new Date().toISOString().split('T')[0],
      status: 'completed',
      notes: ''
    });
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedExpense(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData({
      ...paymentData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      let updatedExpense;
      if (selectedExpense) {
        updatedExpense = await expenseService.updateExpense(selectedExpense._id, expenseData);
        onExpensesChange(safeExpenses.map(exp => exp._id === selectedExpense._id ? updatedExpense : exp));
      } else {
        updatedExpense = await expenseService.createExpense(expenseData);
        onExpensesChange([...safeExpenses, updatedExpense]);
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving expense:', error);
      // Handle error (show notification, etc.)
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const payment = {
        ...paymentData,
        amount: parseFloat(paymentData.amount)
      };

      const updatedExpense = await expenseService.recordPayment(selectedExpense._id, payment);
      onExpensesChange(safeExpenses.map(exp => exp._id === selectedExpense._id ? updatedExpense : exp));
      handleClosePaymentDialog();
    } catch (error) {
      console.error('Error recording payment:', error);
      // Handle error (show notification, etc.)
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await expenseService.deleteExpense(id);
        onExpensesChange(safeExpenses.filter(exp => exp._id !== id));
      } catch (error) {
        console.error('Error deleting expense:', error);
        // Handle error (show notification, etc.)
      }
    }
  };

  // Lock background scroll when any modal is open
  useEffect(() => {
    const anyOpen = openDialog || openPaymentDialog || openGuidedWizard;
    try {
      if (anyOpen) {
        document.documentElement.classList.add('overflow-hidden');
        document.body.classList.add('overflow-hidden');
      } else {
        document.documentElement.classList.remove('overflow-hidden');
        document.body.classList.remove('overflow-hidden');
      }
    } catch (_) {}
    return () => {
      try {
        document.documentElement.classList.remove('overflow-hidden');
        document.body.classList.remove('overflow-hidden');
      } catch (_) {}
    };
  }, [openDialog, openPaymentDialog, openGuidedWizard]);

  // no-op: we now position dialogs using top offsets

  // Calculate totals
  const totalMonthly = useMemo(() => {
    return filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  }, [filteredExpenses]);

  return (
    <div className="bg-white rounded-lg shadow p-3 sm:p-6">
      <Suspense fallback={null}>
        <ExpenseStatsDashboard expenses={safeExpenses} netMonthly={netMonthly} />
      </Suspense>
      
      {/* Header with totals, view toggle, and add button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Expenses</h2>
          <p className="text-sm text-gray-500 mt-1">
            Total: <span className="font-medium text-gray-900">{formatCurrency(totalMonthly)}/month</span>
          </p>
        </div>
        <div className="mt-3 sm:mt-0 flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden shrink-0">
        <button
              onClick={() => setViewMode('cards')}
              className={`sm:px-3 px-2 sm:py-1.5 py-1 text-xs sm:text-sm ${viewMode==='cards' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              title="Card list view"
            >
              <ViewModuleIcon className="sm:hidden mr-0" fontSize="small" />
              <span className="hidden sm:inline">Cards</span>
            </button>
            <button
              onClick={() => setViewMode('sliders')}
              className={`sm:px-3 px-2 sm:py-1.5 py-1 text-xs sm:text-sm ${viewMode==='sliders' ? 'bg-gray-100 text-gray-900' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              title="Adjust amounts with sliders"
            >
              <TuneIcon className="sm:hidden mr-0" fontSize="small" />
              <span className="hidden sm:inline">Sliders</span>
            </button>
          </div>
          {GUIDED_EXPENSE_ENABLED && (
            <button
              onClick={() => { setGuidedStep(0); setOpenGuidedWizard(true); }}
              className="sm:px-4 px-2 sm:py-2 py-1 border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 flex items-center text-xs sm:text-sm shrink-0"
              title="Guided setup to quickly add common expenses"
            >
              <AddIcon fontSize="small" className="mr-1" />
              <span className="sm:hidden">Guided</span>
              <span className="hidden sm:inline">Guided Expense</span>
            </button>
          )}
          <button
            onClick={(e) => handleOpenDialog(null, '', e)}
            className="sm:px-4 px-2 sm:py-2 py-1 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-100 flex items-center text-xs sm:text-sm shrink-0"
        >
          <AddIcon fontSize="small" className="mr-1" />
          <span className="sm:hidden">Add</span>
          <span className="hidden sm:inline">Add Expense</span>
        </button>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-3 mb-6 flex items-center text-base">
        <div className="mr-4 flex items-center">
          <WarningIcon fontSize="small" className="text-amber-500 mr-1" />
          <span className="text-gray-700">Past due date</span>
        </div>
        {viewMode === 'cards' ? null : (
          <>
            <div className="h-5 border-r border-gray-300 mx-2"></div>
            <div className="flex items-center text-gray-700">
              Adjust sliders then Save per row or Save Changes.
        </div>
          </>
        )}
      </div>

      {/* Expense type tabs and sorting */}
      <div className="mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab(0)}
            className={`py-2 px-4 text-sm font-medium border-b-2 ${
              activeTab === 0 
                ? 'border-indigo-600 text-indigo-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Fixed Expenses
          </button>
          <button
            onClick={() => setActiveTab(1)}
            className={`py-2 px-4 text-sm font-medium border-b-2 ${
              activeTab === 1 
                ? 'border-indigo-600 text-indigo-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Variable Expenses
          </button>
          <button
            onClick={() => setActiveTab(2)}
            className={`py-2 px-4 text-sm font-medium border-b-2 ${
              activeTab === 2 
                ? 'border-indigo-600 text-indigo-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Expenses
          </button>
        </div>
        {/* Sort control for Cards and Sliders views */}
        {(viewMode === 'sliders' || viewMode === 'cards') && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <span className="text-gray-600">Sort:</span>
            <select
              value={sortMode}
              onChange={(e)=>setSortMode(e.target.value)}
              className="border border-gray-200 rounded-md px-2 py-1 text-gray-800 bg-white"
            >
              <option value="none">Default</option>
              <option value="amountDesc">Value/price High → Low</option>
              <option value="amountAsc">Value/price Low → High</option>
              <option value="nameAsc">Alphabetical A → Z</option>
              <option value="nameDesc">Alphabetical Z → A</option>
              <option value="pctDesc">Percentage High → Low</option>
              <option value="pctAsc">Percentage Low → High</option>
            </select>
          </div>
        )}
      </div>

      {/* No expenses message */}
      {filteredExpenses.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            No {activeTab === 0 ? 'fixed' : activeTab === 1 ? 'variable' : ''} expenses found.
          </p>
          <button
            onClick={() => handleOpenDialog()}
            className="mt-3 px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg text-sm hover:bg-indigo-100"
          >
            Add your first expense
          </button>
        </div>
      ) : viewMode === 'cards' ? (
          <div className="space-y-6">
          {/* Render expenses grouped by category in a compact tile grid */}
            {Object.entries(groupedExpenses).map(([category, expenses]) => (
            <div key={category} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-3">
                <div className="flex items-center min-w-0">
                    {React.createElement(CATEGORY_CONFIG[category]?.icon || CATEGORY_CONFIG['Other'].icon, {
                      style: { color: CATEGORY_CONFIG[category]?.color || CATEGORY_CONFIG['Other'].color },
                      className: "mr-2"
                    })}
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{category}</h3>
                  </div>
                <div className="flex items-center gap-2 flex-shrink-0 text-sm text-gray-700">
                  <span>
                    {formatCurrency(expenses.reduce((s,e)=>s+(e.amount||0),0))}
                  </span>
                  <button
                    onClick={(e) => handleOpenDialog(null, category, e)}
                    className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-50"
                  >
                    <AddIcon sx={{ fontSize: 16 }} className="mr-1 hidden sm:inline" />
                    Add
                  </button>
                </div>
              </div>
              {(() => {
                const color = CATEGORY_CONFIG[category]?.color || '#6366F1';
                const sorted = [...expenses].sort((a,b)=>{
                  if (sortMode === 'amountDesc') return (b.amount||0) - (a.amount||0);
                  if (sortMode === 'amountAsc') return (a.amount||0) - (b.amount||0);
                  if (sortMode === 'nameAsc') return String(a.name||'').localeCompare(String(b.name||''));
                  if (sortMode === 'nameDesc') return String(b.name||'').localeCompare(String(a.name||''));
                  if (sortMode === 'pctDesc') {
                    const pa = netMonthly>0 ? ((a.amount||0)/netMonthly) : 0;
                    const pb = netMonthly>0 ? ((b.amount||0)/netMonthly) : 0;
                    return pb - pa;
                  }
                  if (sortMode === 'pctAsc') {
                    const pa = netMonthly>0 ? ((a.amount||0)/netMonthly) : 0;
                    const pb = netMonthly>0 ? ((b.amount||0)/netMonthly) : 0;
                    return pa - pb;
                  }
                  return 0;
                });
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {sorted.map(exp => (
                      <div
                        key={exp._id}
                        className="rounded-md border bg-white p-3 transition transform hover:-translate-y-0.5 hover:shadow-md"
                        style={{ borderColor: '#E5E7EB', borderLeftColor: color, borderLeftWidth: 4 }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex items-start gap-2">
                            {React.createElement(CATEGORY_CONFIG[category]?.icon || CATEGORY_CONFIG['Other'].icon, {
                              style: { color },
                              className: "mt-0.5"
                            })}
                            <div className="min-w-0">
                              <div className="text-base font-medium text-gray-900 truncate">{exp.name}</div>
                              <div className="text-sm text-gray-500 truncate">{exp.type || 'expense'} · {exp.frequency || 'monthly'}</div>
                            </div>
                          </div>
                          <div className="text-base font-semibold ml-2 whitespace-nowrap" style={{ color }}>{formatCurrency(exp.amount || 0)}</div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="text-xs text-gray-500 truncate">Due {exp.dueDate ? formatDate(exp.dueDate) : (exp.dayOfMonth ? `${formatOrdinal(exp.dayOfMonth)}` : '—')}</div>
                          <div className="flex items-center gap-2 opacity-90">
                        <button onClick={(e) => handleOpenDialog(exp, '', e)} className="px-2 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 text-gray-700">Edit</button>
                            <button onClick={() => handleDelete(exp._id)} className="px-2 py-1 text-sm border border-rose-200 rounded hover:bg-rose-50 text-rose-600">Delete</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              </div>
            ))}
          </div>
      ) : (
        <div className="space-y-5">
          <div className="text-base text-gray-700 -mt-2">Adjust amounts. Percentages are of your combined monthly net.</div>
          {Object.entries(groupedExpenses).map(([category, expenses]) => {
            const subtotal = expenses.reduce((s,e)=>s+(e.amount||0),0);
            const pctCat = netMonthly>0 ? Math.round((subtotal/netMonthly)*100) : 0;
            const [low, high] = CATEGORY_RANGES[category] || [1,10];
            // Apply sorting
            const sorted = [...expenses].sort((a,b)=>{
              if (sortMode === 'amountDesc') return (b.amount||0) - (a.amount||0);
              if (sortMode === 'amountAsc') return (a.amount||0) - (b.amount||0);
              if (sortMode === 'nameAsc') return String(a.name||'').localeCompare(String(b.name||''));
              if (sortMode === 'nameDesc') return String(b.name||'').localeCompare(String(a.name||''));
              if (sortMode === 'pctDesc') {
                const pa = netMonthly>0 ? ((editedAmounts[a._id] ?? a.amount ?? 0)/netMonthly) : 0;
                const pb = netMonthly>0 ? ((editedAmounts[b._id] ?? b.amount ?? 0)/netMonthly) : 0;
                return pb - pa;
              }
              if (sortMode === 'pctAsc') {
                const pa = netMonthly>0 ? ((editedAmounts[a._id] ?? a.amount ?? 0)/netMonthly) : 0;
                const pb = netMonthly>0 ? ((editedAmounts[b._id] ?? b.amount ?? 0)/netMonthly) : 0;
                return pa - pb;
              }
              return 0;
            });
            return (
              <div key={category} className="rounded-lg border border-gray-200 bg-gray-50">
                <div
                  className="flex items-center justify-between px-3 py-3 rounded-t-lg"
                  style={{ backgroundColor: CATEGORY_CONFIG[category]?.bgColor || '#F3F4F6' }}
                >
                  <div className="flex items-center min-w-0">
                    {React.createElement(CATEGORY_CONFIG[category]?.icon || CATEGORY_CONFIG['Other'].icon, {
                      style: { color: CATEGORY_CONFIG[category]?.color || CATEGORY_CONFIG['Other'].color },
                      className: "mr-2"
                    })}
                    <h3 className="text-lg font-semibold truncate" style={{ color: CATEGORY_CONFIG[category]?.color || '#111827' }}>{category}</h3>
                  </div>
                  <div className="text-sm" style={{ color: CATEGORY_CONFIG[category]?.color || '#374151' }}>{formatCurrency(subtotal)} · {pctCat}%</div>
                </div>
                <div className="space-y-3 p-3">
                  {sorted.map(exp => {
                    const baseline = sliderBaselines[exp._id] ?? exp.amount ?? 0;
                    const current = editedAmounts[exp._id] ?? baseline;
                    const highAbs = Math.round((high * netMonthly) / 100);
                    const baseAbs = Math.round((baseline || 0) * 1.5);
                    const sliderMin = 0;
                    // Absolute hard cap at 50% of net to avoid runaway
                    const hardCap = Math.max(0, Math.round((netMonthly || 0) * 0.5));
                    const sliderMax = Math.min(Math.max(highAbs, baseAbs), hardCap || Math.max(highAbs, baseAbs));
                    const value = Math.max(sliderMin, Math.min(current, sliderMax));
                    const pct = netMonthly>0 ? (value/netMonthly)*100 : 0;
                    const band = getBandFor(category, pct);
                    const isHigh = pct > (CATEGORY_RANGES[category]?.[1] ?? 10);
                    return (
                      <div key={exp._id} className={`rounded-md p-4 ${isHigh ? 'bg-rose-50' : 'bg-white'}`}>
                        <div className="flex items-center justify-between">
                          <div className="text-base font-medium text-gray-900 truncate">
                            {exp.name}
                            <span className="ml-2 text-sm text-gray-500">{category}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-sm text-gray-500">{pct.toFixed(1)}% <span className={`${band.color}`}>· {band.label}</span></div>
                            <button
                              onClick={(e) => handleOpenDialog(exp, '', e)}
                              className="text-sm px-2 py-1 border border-gray-200 text-gray-700 rounded hover:bg-gray-50"
                              title="Edit expense"
                            >Edit</button>
                            <button
                              onClick={() => handleDelete(exp._id)}
                              className="text-sm px-2 py-1 border border-rose-200 text-rose-600 rounded hover:bg-rose-50"
                              title="Delete expense"
                            >Delete</button>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="range"
                            min={sliderMin}
                            max={sliderMax}
                            value={value}
                            onChange={(e)=>{
                              const v = Math.max(sliderMin, Math.min(parseFloat(e.target.value||'0'), sliderMax));
                              setEditedAmounts(prev => ({...prev, [exp._id]: v}));
                            }}
                            className="w-full tw-slider"
                          />
                        </div>
                        <div className="mt-3 flex items-center gap-3">
                          <div className="text-base text-gray-900 font-medium">{formatCurrency(value)}</div>
                          <div className="text-sm text-gray-500">/mo · Low {low}% · High {high}%</div>
                          {(editedAmounts[exp._id] !== undefined && editedAmounts[exp._id] !== baseline) && (
                            <>
                              <button
                                onClick={async ()=>{
                                  try {
                                    const amt = editedAmounts[exp._id];
                                    const payload = {
                                      name: exp.name,
                                      amount: Number.isFinite(amt) ? amt : parseFloat(String(amt || 0)),
                                      type: (exp.type || 'fixed'),
                                      category: exp.category || 'Other',
                                      frequency: (exp.frequency || 'monthly').toLowerCase(),
                                      isRecurring: exp.isRecurring !== undefined ? exp.isRecurring : true,
                                      dayOfMonth: exp.dayOfMonth ?? 1,
                                      status: exp.status || 'active'
                                    };
                                    await expenseService.updateExpense(exp._id, payload);
                                    const refreshed = await expenseService.getAllExpenses(true);
                                    onExpensesChange(refreshed);
                                    setEditedAmounts(prev=>{ const p={...prev}; delete p[exp._id]; return p; });
                                    setSliderBaselines(prev=>({ ...prev, [exp._id]: payload.amount }));
                                  } catch(err) { console.error('Save failed', err); }
                                }}
                                className="text-sm px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                              >Save</button>
                              <button
                                onClick={()=>{
                                  setEditedAmounts(prev=>{ const p={...prev}; delete p[exp._id]; return p; });
                                }}
                                className="text-sm px-3 py-1 border border-gray-200 text-gray-700 rounded hover:bg-gray-50"
                              >Revert</button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="flex justify-end">
            <button
              disabled={savingAll}
              onClick={async ()=>{
                try {
                  setSavingAll(true);
                  const updates = Object.entries(editedAmounts).filter(([,v])=>!Number.isNaN(parseFloat(v)));
                  for (const [id, amt] of updates) {
                    const expObj = safeExpenses.find(e => e._id === id);
                    if (!expObj) continue;
                    const payload = {
                      name: expObj.name,
                      amount: Number.isFinite(amt) ? amt : parseFloat(String(amt || 0)),
                      type: (expObj.type || 'fixed'),
                      category: expObj.category || 'Other',
                      frequency: (expObj.frequency || 'monthly').toLowerCase(),
                      isRecurring: expObj.isRecurring !== undefined ? expObj.isRecurring : true,
                      dayOfMonth: expObj.dayOfMonth ?? 1,
                      status: expObj.status || 'active'
                    };
                    await expenseService.updateExpense(id, payload);
                  }
                  const refreshed = await expenseService.getAllExpenses(true);
                  onExpensesChange(refreshed);
                  setEditedAmounts({});
                } catch (e) {
                  console.error('Failed to save slider edits', e);
                } finally {
                  setSavingAll(false);
                }
              }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingAll ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Expense Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-gray-200 bg-opacity-70 backdrop-blur-sm z-50">
          <div className="absolute left-1/2 -translate-x-1/2 w-full max-w-md mx-4" style={{ top: `${modalAnchorRef.current}px` }}>
            <div className="bg-white rounded-lg shadow-lg p-6 w-full">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              {selectedExpense ? 'Edit Expense' : 'Add Expense'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expense Name<span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount<span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                        $
                      </span>
                      <input 
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type<span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                      required
                    >
                      <option value="fixed">Fixed</option>
                      <option value="variable">Variable</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category<span className="text-red-500">*</span>
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                      required
                    >
                      <option value="">Select category</option>
                      {Object.keys(CATEGORY_CONFIG).map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frequency<span className="text-red-500">*</span>
                    </label>
                    <select
                      name="frequency"
                      value={formData.frequency}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                      required
                    >
                      {FREQUENCIES.map(freq => (
                        <option key={freq.value} value={freq.value}>{freq.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day of Month Due<span className="text-red-500">*</span>
                    </label>
                    <select
                      name="dayOfMonth"
                      value={formData.dayOfMonth || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                      required
                    >
                      <option value="">Select day</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{formatOrdinal(day)}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status<span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                      required
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea 
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-100"
                >
                  {selectedExpense ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Guided Expense Wizard (feature-flagged) */}
      {GUIDED_EXPENSE_ENABLED && openGuidedWizard && createPortal((
        <div className="fixed inset-0 bg-gray-200 bg-opacity-70 backdrop-blur-sm flex justify-center items-center overflow-hidden py-6 sm:py-10 z-[9999]">
          <div className="bg-white rounded-lg shadow-2xl ring-1 ring-black/5 p-0 w-full max-w-3xl mx-4 max-h-[92vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Guided Expense Setup</h3>
                  <p className="text-xs text-gray-500 mt-1">Step {guidedStep + 1} of {TOTAL_WIZARD_STEPS}</p>
                </div>
                <button
                  onClick={() => setOpenGuidedWizard(false)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  <XMarkIcon fontSize="small" />
                </button>
              </div>
              {/* Progress bar */}
              <div className="mt-3 h-4 w-full rounded-full bg-indigo-100 overflow-hidden">
                <div
                  className="h-4 rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-teal-400 transition-all"
                  style={{ width: `${((guidedStep + 1) / TOTAL_WIZARD_STEPS) * 100}%` }}
                />
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-5" id="guided-wizard-content">
              {guidedStep === 0 && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Check Income</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Select your active income(s). You can also add a new one. We'll use the combined monthly net for recommendations.
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                    {existingIncomes?.length > 0 ? (
                      <div className="max-h-48 overflow-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {existingIncomes.map(inc => {
                            const selected = selectedIncomeIds.includes(inc._id);
                            return (
                              <button key={inc._id} type="button" onClick={()=> setSelectedIncomeIds(prev => selected ? prev.filter(id => id !== inc._id) : [...prev, inc._id])}
                                className={`w-full text-left px-3 py-2 rounded-md border text-sm transition ${selected ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}>
                                <div className="flex items-center justify-between">
                                  <span className="truncate mr-2">{inc.name || 'Income'}</span>
                                  <span className="whitespace-nowrap">
                                    {inc.__netMonthly != null ? (
                                      <>
                                        <span className="font-medium">${Math.round(inc.__netMonthly).toLocaleString()}</span>
                                        <span className="text-gray-500"> net</span>
                                      </>
                                    ) : (
                                      <span className="text-gray-400">est. net n/a</span>
                                    )}
                                    <span className="text-gray-400"> / </span>
                                    <span>${Math.round(inc.__grossMonthly || 0).toLocaleString()} gross</span>
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No active incomes found. Add one below.</div>
                    )}
                    <button type="button" onClick={() => { setAddIncomeOpen(true); setGuidedError(''); }} className="text-indigo-600 hover:text-indigo-800 text-sm">Don't see your income? Add Income</button>
                    {guidedError && (
                      <div className="mt-3 text-sm text-red-600">{guidedError}</div>
                    )}
                  </div>
                </div>
              )}
              {guidedStep === 1 && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Household Profile</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Housing</label>
                      <div className="grid grid-cols-3 gap-2">
                        <ToggleCard selected={household.housing === 'own'} onClick={()=> toggleHousing('own')} label="Own" emoji="🏠" color="emerald" />
                        <ToggleCard selected={household.housing === 'rent'} onClick={()=> toggleHousing('rent')} label="Rent" emoji="🏡" color="sky" />
                        <ToggleCard selected={household.housing === 'neither'} onClick={()=> toggleHousing('neither')} label="Neither" emoji="🛋️" color="amber" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Household Size</label>
                      <input type="number" min="1" value={household.householdSize} onChange={(e) => setHousehold(prev => ({ ...prev, householdSize: parseInt(e.target.value || '1') }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                    </div>

                    {/* Location & Home details */}
                  <div className="grid grid-cols-2 gap-3 sm:col-span-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ZIP / Postal</label>
                        <input type="text" value={household.zip} onChange={(e)=> setHousehold(prev=> ({...prev, zip: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input type="text" value={household.state} onChange={(e)=> setHousehold(prev=> ({...prev, state: e.target.value.toUpperCase().slice(0,2)}))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" placeholder="CA" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Square Footage (optional)</label>
                        <input type="number" value={household.squareFootage} onChange={(e)=> setHousehold(prev=> ({...prev, squareFootage: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price (optional)</label>
                        <input type="number" value={household.purchasePrice} onChange={(e)=> setHousehold(prev=> ({...prev, purchasePrice: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Market Price (est, optional)</label>
                        <input type="number" value={household.marketPrice} onChange={(e)=> setHousehold(prev=> ({...prev, marketPrice: e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                      </div>
                    </div>

                    {/* Amount Inputs: Housing - required fields based on selection */}
                    <div className="sm:col-span-2">
                      <h5 className="text-sm font-medium text-gray-900 mt-1 mb-2">Housing Costs</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {household.housing === 'rent' && (
                          <>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Rent $/mo<span className="text-red-500">*</span></label>
                              <input required type="number" value={household.rentAmount} onChange={(e)=> setHousehold(prev=> ({...prev, rentAmount: e.target.value}))} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Renter's Insurance</label>
                              <ToggleCard selected={household.hasRentersInsurance} onClick={()=> setHousehold(prev=> ({...prev, hasRentersInsurance: !prev.hasRentersInsurance}))} label={household.hasRentersInsurance ? 'Yes' : 'No'} emoji={household.hasRentersInsurance ? '🛡️' : '🚫'} color={household.hasRentersInsurance ? 'rose' : 'indigo'} />
                            </div>
                            {household.hasRentersInsurance && (
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Renter's Insurance $/mo</label>
                                <input required type="number" value={household.rentersInsuranceMonthly} onChange={(e)=> setHousehold(prev=> ({...prev, rentersInsuranceMonthly: e.target.value}))} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                              </div>
                            )}
                          </>
                        )}
                        {household.housing === 'own' && (
                          <>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Mortgage $/mo<span className="text-red-500">*</span></label>
                              <input required type="number" value={household.mortgageAmount} onChange={(e)=> setHousehold(prev=> ({...prev, mortgageAmount: e.target.value}))} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Home Insurance $/mo<span className="text-red-500">*</span></label>
                              <input required type="number" value={household.homeInsuranceMonthly} onChange={(e)=> setHousehold(prev=> ({...prev, homeInsuranceMonthly: e.target.value}))} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Property Tax in Escrow?</label>
                              <ToggleCard selected={household.propertyTaxInMortgage} onClick={()=> setHousehold(prev=> ({...prev, propertyTaxInMortgage: !prev.propertyTaxInMortgage}))} label={household.propertyTaxInMortgage ? 'Yes' : 'No'} emoji={household.propertyTaxInMortgage ? '🏦' : '🧾'} color={household.propertyTaxInMortgage ? 'emerald' : 'rose'} />
                            </div>
                            {!household.propertyTaxInMortgage && (
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Property Tax $/mo<span className="text-red-500">*</span></label>
                                <input required type="number" value={household.propertyTaxMonthly} onChange={(e)=> setHousehold(prev=> ({...prev, propertyTaxMonthly: e.target.value}))} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                              </div>
                            )}
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">HOA?</label>
                              <ToggleCard selected={household.hoa} onClick={()=> setHousehold(prev=> ({...prev, hoa: !prev.hoa}))} label={household.hoa ? 'Yes' : 'No'} emoji={household.hoa ? '🏘️' : '🚫'} color={household.hoa ? 'sky' : 'indigo'} />
                            </div>
                            {household.hoa && (
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">HOA $/mo<span className="text-red-500">*</span></label>
                                <input required type="number" value={household.hoaMonthly} onChange={(e)=> setHousehold(prev=> ({...prev, hoaMonthly: e.target.value}))} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {guidedStep === 2 && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Fixed Expenses</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Utilities */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Utilities</label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={fixed.hasElectric} onClick={()=> setFixed(prev=> ({...prev, hasElectric: !prev.hasElectric, electricMonthly: !prev.hasElectric ? prev.electricMonthly : ''}))} label="Electric" emoji="💡" color="amber" />
                          {fixed.hasElectric && (
                            <>
                              <label className="block text-xs text-gray-600">Electric $/mo</label>
                              <input type="number" value={fixed.electricMonthly} onChange={(e)=> setFixed(prev=> ({...prev, electricMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={fixed.hasGas} onClick={()=> setFixed(prev=> ({...prev, hasGas: !prev.hasGas, gasMonthly: !prev.hasGas ? prev.gasMonthly : ''}))} label="Gas" emoji="🔥" color="rose" />
                          {fixed.hasGas && (
                            <>
                              <label className="block text-xs text-gray-600">Gas $/mo</label>
                              <input type="number" value={fixed.gasMonthly} onChange={(e)=> setFixed(prev=> ({...prev, gasMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={fixed.hasWater} onClick={()=> setFixed(prev=> ({...prev, hasWater: !prev.hasWater, waterMonthly: !prev.hasWater ? prev.waterMonthly : ''}))} label="Water" emoji="🚿" color="sky" />
                          {fixed.hasWater && (
                            <>
                              <label className="block text-xs text-gray-600">Water/Trash $/mo</label>
                              <input type="number" value={fixed.waterMonthly} onChange={(e)=> setFixed(prev=> ({...prev, waterMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Connectivity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Connectivity</label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={fixed.hasInternet} onClick={()=> setFixed(prev=> ({...prev, hasInternet: !prev.hasInternet, internetMonthly: !prev.hasInternet ? prev.internetMonthly : ''}))} label="Internet" emoji="🌐" color="indigo" />
                          {fixed.hasInternet && (
                            <>
                              <label className="block text-xs text-gray-600">Internet $/mo</label>
                              <input type="number" value={fixed.internetMonthly} onChange={(e)=> setFixed(prev=> ({...prev, internetMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={fixed.hasMobile} onClick={()=> setFixed(prev=> ({...prev, hasMobile: !prev.hasMobile, mobileMonthly: !prev.hasMobile ? prev.mobileMonthly : ''}))} label="Mobile" emoji="📱" color="emerald" />
                          {fixed.hasMobile && (
                            <>
                              <label className="block text-xs text-gray-600">Mobile $/mo</label>
                              <input type="number" value={fixed.mobileMonthly} onChange={(e)=> setFixed(prev=> ({...prev, mobileMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={fixed.hasSubscriptions} onClick={()=> setFixed(prev=> ({...prev, hasSubscriptions: !prev.hasSubscriptions, subscriptionsMonthly: !prev.hasSubscriptions ? prev.subscriptionsMonthly : ''}))} label="Subs" emoji="📺" color="amber" />
                          {fixed.hasSubscriptions && (
                            <>
                              <label className="block text-xs text-gray-600">Subscriptions $/mo</label>
                              <input type="number" value={fixed.subscriptionsMonthly} onChange={(e)=> setFixed(prev=> ({...prev, subscriptionsMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Insurance */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Insurance</label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={fixed.hasHealthInsurance} onClick={()=> setFixed(prev=> ({...prev, hasHealthInsurance: !prev.hasHealthInsurance, healthInsuranceMonthly: !prev.hasHealthInsurance ? prev.healthInsuranceMonthly : '', healthOOPMonthly: !prev.hasHealthInsurance ? prev.healthOOPMonthly : ''}))} label="Health" emoji="🩺" color="rose" />
                          {fixed.hasHealthInsurance && (
                            <>
                              <label className="block text-xs text-gray-600">Health Ins. $/mo</label>
                              <input type="number" value={fixed.healthInsuranceMonthly} onChange={(e)=> setFixed(prev=> ({...prev, healthInsuranceMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                              <label className="block text-xs text-gray-600">Health OOP $/mo</label>
                              <input type="number" value={fixed.healthOOPMonthly} onChange={(e)=> setFixed(prev=> ({...prev, healthOOPMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={fixed.hasAutoInsurance} onClick={()=> setFixed(prev=> ({...prev, hasAutoInsurance: !prev.hasAutoInsurance, autoInsuranceMonthly: !prev.hasAutoInsurance ? prev.autoInsuranceMonthly : ''}))} label="Auto" emoji="🚗" color="sky" />
                          {fixed.hasAutoInsurance && (
                            <>
                              <label className="block text-xs text-gray-600">Auto Ins. $/mo</label>
                              <input type="number" value={fixed.autoInsuranceMonthly} onChange={(e)=> setFixed(prev=> ({...prev, autoInsuranceMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                        {/* Auto Pay removed per request */}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {guidedStep === 3 && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Variable Expenses</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Food */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Food</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={variable.hasGroceries} onClick={()=> setVariable(prev=> ({...prev, hasGroceries: !prev.hasGroceries, groceriesMonthly: !prev.hasGroceries ? prev.groceriesMonthly : ''}))} label="Groceries" emoji="🛒" color="emerald" />
                          {variable.hasGroceries && (
                            <>
                              <label className="block text-xs text-gray-600">Groceries $/mo</label>
                              <input type="number" value={variable.groceriesMonthly} onChange={(e)=> setVariable(prev=> ({...prev, groceriesMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={variable.hasDining} onClick={()=> setVariable(prev=> ({...prev, hasDining: !prev.hasDining, diningMonthly: !prev.hasDining ? prev.diningMonthly : ''}))} label="Dining" emoji="🍽️" color="amber" />
                          {variable.hasDining && (
                            <>
                              <label className="block text-xs text-gray-600">Dining $/mo</label>
                              <input type="number" value={variable.diningMonthly} onChange={(e)=> setVariable(prev=> ({...prev, diningMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Transportation variable */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transportation</label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={variable.hasFuel} onClick={()=> setVariable(prev=> ({...prev, hasFuel: !prev.hasFuel, fuelMonthly: !prev.hasFuel ? prev.fuelMonthly : ''}))} label="Fuel" emoji="⛽" color="rose" />
                          {variable.hasFuel && (
                            <>
                              <label className="block text-xs text-gray-600">Fuel $/mo</label>
                              <input type="number" value={variable.fuelMonthly} onChange={(e)=> setVariable(prev=> ({...prev, fuelMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={variable.hasMaintenance} onClick={()=> setVariable(prev=> ({...prev, hasMaintenance: !prev.hasMaintenance, maintenanceMonthly: !prev.hasMaintenance ? prev.maintenanceMonthly : ''}))} label="Maint" emoji="🛠️" color="sky" />
                          {variable.hasMaintenance && (
                            <>
                              <label className="block text-xs text-gray-600">Maintenance $/mo</label>
                              <input type="number" value={variable.maintenanceMonthly} onChange={(e)=> setVariable(prev=> ({...prev, maintenanceMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Childcare */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Childcare</label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex flex-col gap-2">
                            <ToggleCard selected={variable.hasChildcare} onClick={()=> setVariable(prev=> ({...prev, hasChildcare: !prev.hasChildcare, childcareMonthly: !prev.hasChildcare ? prev.childcareMonthly : ''}))} label="Childcare" emoji="🧸" color="rose" />
                            {variable.hasChildcare && (
                              <>
                                <label className="block text-xs text-gray-600">Childcare $/mo</label>
                                <input type="number" value={variable.childcareMonthly} onChange={(e)=> setVariable(prev=> ({...prev, childcareMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                              </>
                            )}
                          </div>
                        </div>
                    </div>

                    {/* Pets */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pets</label>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex flex-col gap-2">
                          <ToggleCard selected={variable.hasPets} onClick={()=> setVariable(prev=> ({...prev, hasPets: !prev.hasPets, petFoodMonthly: !prev.hasPets ? prev.petFoodMonthly : '', vetMonthly: !prev.hasPets ? prev.vetMonthly : '', petInsuranceMonthly: !prev.hasPets ? prev.petInsuranceMonthly : ''}))} label="Pets" emoji="🐾" color="emerald" />
                          {variable.hasPets && (
                            <>
                              <label className="block text-xs text-gray-600">Pet Food $/mo</label>
                              <input type="number" value={variable.petFoodMonthly} onChange={(e)=> setVariable(prev=> ({...prev, petFoodMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                              <label className="block text-xs text-gray-600">Vet $/mo</label>
                              <input type="number" value={variable.vetMonthly} onChange={(e)=> setVariable(prev=> ({...prev, vetMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                              <label className="block text-xs text-gray-600">Pet Insurance $/mo</label>
                              <input type="number" value={variable.petInsuranceMonthly} onChange={(e)=> setVariable(prev=> ({...prev, petInsuranceMonthly: e.target.value}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Vehicles */}
                    <div className="sm:col-span-2">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Vehicles</label>
                        <button type="button" onClick={()=> setVariable(prev=> ({...prev, vehicles: [...prev.vehicles, { ownership:'own', year:'', make:'', model:'', payment:'' }]}))} className="text-indigo-600 hover:text-indigo-800 text-sm">+ Add Vehicle</button>
                      </div>
                      <div className="space-y-2">
                        {variable.vehicles.map((v, idx) => (
                          <div key={idx} className="grid grid-cols-6 gap-2 items-end">
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-600 mb-1">Ownership</label>
                              <div className="grid grid-cols-3 gap-2">
                                <ToggleCard selected={v.ownership === 'own'} onClick={()=> setVariable(prev=> ({...prev, vehicles: prev.vehicles.map((x,i)=> i===idx? {...x, ownership: 'own'}: x)}))} label="Own" emoji="🚗" color="emerald" />
                                <ToggleCard selected={v.ownership === 'finance'} onClick={()=> setVariable(prev=> ({...prev, vehicles: prev.vehicles.map((x,i)=> i===idx? {...x, ownership: 'finance'}: x)}))} label="Finance" emoji="💳" color="sky" />
                                <ToggleCard selected={v.ownership === 'lease'} onClick={()=> setVariable(prev=> ({...prev, vehicles: prev.vehicles.map((x,i)=> i===idx? {...x, ownership: 'lease'}: x)}))} label="Lease" emoji="📄" color="amber" />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Year</label>
                              <input type="text" value={v.year} onChange={(e)=> setVariable(prev=> ({...prev, vehicles: prev.vehicles.map((x,i)=> i===idx? {...x, year: e.target.value }: x)}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Make</label>
                              <input type="text" value={v.make} onChange={(e)=> setVariable(prev=> ({...prev, vehicles: prev.vehicles.map((x,i)=> i===idx? {...x, make: e.target.value }: x)}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Model</label>
                              <input type="text" value={v.model} onChange={(e)=> setVariable(prev=> ({...prev, vehicles: prev.vehicles.map((x,i)=> i===idx? {...x, model: e.target.value }: x)}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Monthly Payment</label>
                              <input type="number" value={v.payment} onChange={(e)=> setVariable(prev=> ({...prev, vehicles: prev.vehicles.map((x,i)=> i===idx? {...x, payment: e.target.value }: x)}))} className="w-full px-2 py-2 border border-gray-300 rounded-md text-gray-900" placeholder={v.ownership === 'own' ? '0.00' : '0.00 (if financed/leased)'} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {guidedStep === 4 && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Review & Adjust</h4>
                  <p className="text-sm text-gray-600 mb-3">Adjust amounts if needed. Percentages are of your combined monthly net.</p>
                  <div className="space-y-2">
                    {reviewItems.length === 0 ? (
                      <div className="text-sm text-gray-500">No items to review. Go back to add expenses.</div>
                    ) : (
                      reviewItems.map(item => {
                        const currentRaw = reviewAdjustments[item.key] ?? item.amount;
                        const netMonthly = parseFloat(incomeForm.amount) || 0;
                        // Fixed slider bounds: allow below typical low, cap slightly above 'high' or 1.5x original amount
                        const ranges = {
                          Housing: [20, 35], Utilities: [3, 8], Food: [10, 20], Transportation: [10, 15], Insurance: [3, 10], Healthcare: [2, 8], Entertainment: [1, 4], Other: [0, 3]
                        };
                        const [low, high] = ranges[item.category] || [1, 10];
                        const sliderMin = 0;
                        const highAbs = Math.round((high * netMonthly) / 100);
                        const baseAbs = Math.round((item.amount || 0) * 1.5);
                        const sliderMax = Math.max(highAbs, baseAbs);
                        const current = Math.max(sliderMin, Math.min(currentRaw, sliderMax));
                        const pct = netMonthly > 0 ? (current / netMonthly) * 100 : 0;
                        // simple ranges per category
                        const band = pct < low ? 'low' : pct > high ? 'high' : 'just';
                        const bandColor = band === 'low' ? 'bg-teal-100' : band === 'high' ? 'bg-rose-100' : 'bg-emerald-100';
                        const percentColor = band === 'low' ? 'text-teal-600' : band === 'high' ? 'text-rose-600' : 'text-emerald-600';
                        return (
                          <div key={item.key} className={`border border-gray-200 rounded-md p-3 ${bandColor}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-sm text-gray-900">
                                <span className="font-medium mr-2">{item.name}</span>
                                <span className="text-xs text-gray-500">{item.category}</span>
                              </div>
                              <div className={`text-xs ${percentColor}`}>{pct.toFixed(1)}%</div>
                            </div>
                            <div className="flex items-center gap-3">
                              <input type="range" min={sliderMin} max={sliderMax} value={current}
                                onChange={(e)=> {
                                  const val = Math.max(sliderMin, Math.min(parseFloat(e.target.value || '0'), sliderMax));
                                  setReviewAdjustments(prev => ({...prev, [item.key]: val}));
                                }}
                                className="flex-1 tw-slider"
                                style={{ WebkitAppearance: 'none' }} />
                              <input type="number" value={current}
                                onChange={(e)=> {
                                  const val = Math.max(sliderMin, Math.min(parseFloat(e.target.value || '0'), sliderMax));
                                  setReviewAdjustments(prev => ({...prev, [item.key]: val}));
                                }}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-gray-900" />
                              <span className="text-xs text-gray-400">/mo</span>
                            </div>
                            <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                              <span>Low ({low}%)</span>
                              <span>Just Right</span>
                              <span>High ({high}%)</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => setOpenGuidedWizard(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setGuidedStep(prev => Math.max(prev - 1, 0))}
                  disabled={guidedStep === 0}
                  className={`px-4 py-2 border rounded-md ${guidedStep === 0 ? 'text-gray-400 border-gray-200 bg-white' : 'text-gray-700 border-gray-300 bg-white hover:bg-gray-50'}`}
                >
                  Back
                </button>
                {guidedStep < TOTAL_WIZARD_STEPS - 1 ? (
                  <button
                    onClick={async () => {
                      setGuidedError('');
                      const num = (v) => (v !== '' && v !== null && v !== undefined && !Number.isNaN(parseFloat(v)) && parseFloat(v) > 0);
                      if (guidedStep === 0) {
                        if (selectedIncomeIds.length === 0) { setGuidedError('Select at least one income or add a new one.'); return; }
                        const selected = existingIncomes.filter(inc => selectedIncomeIds.includes(inc._id));
                        const totalNet = selected.reduce((sum, inc) => sum + (inc.__netMonthly || 0), 0);
                        setIncomeForm(prev => ({ ...prev, name: 'Selected Incomes', amount: String(Math.round(totalNet)), frequency: 'monthly' }));
                      }
                      if (guidedStep === 1) {
                        if (!household.housing) { setGuidedError('Select a housing option.'); return; }
                        if (household.housing === 'rent') {
                          if (!num(household.rentAmount)) { setGuidedError('Enter Rent amount.'); return; }
                          if (household.hasRentersInsurance && !num(household.rentersInsuranceMonthly)) { setGuidedError("Enter Renter's Insurance amount."); return; }
                        }
                        if (household.housing === 'own') {
                          if (!num(household.mortgageAmount)) { setGuidedError('Enter Mortgage amount.'); return; }
                          if (!num(household.homeInsuranceMonthly)) { setGuidedError('Enter Home Insurance amount.'); return; }
                          if (!household.propertyTaxInMortgage && !num(household.propertyTaxMonthly)) { setGuidedError('Enter Property Tax amount.'); return; }
                          if (household.hoa && !num(household.hoaMonthly)) { setGuidedError('Enter HOA amount.'); return; }
                        }
                      }
                      if (guidedStep === 2) {
                        // Validate toggled fixed items have amounts
                        if ((fixed.hasElectric && !num(fixed.electricMonthly)) ||
                            (fixed.hasGas && !num(fixed.gasMonthly)) ||
                            (fixed.hasWater && !num(fixed.waterMonthly)) ||
                            (fixed.hasInternet && !num(fixed.internetMonthly)) ||
                            (fixed.hasMobile && !num(fixed.mobileMonthly)) ||
                            (fixed.hasSubscriptions && !num(fixed.subscriptionsMonthly)) ||
                            (fixed.hasHealthInsurance && !num(fixed.healthInsuranceMonthly)) ||
                            (fixed.hasAutoInsurance && !num(fixed.autoInsuranceMonthly))) {
                          setGuidedError('Please enter amounts for enabled fixed items.');
                          return;
                        }
                      }
                      if (guidedStep === 3) {
                        // Validate variable toggles have amounts
                        if ((variable.hasGroceries && !num(variable.groceriesMonthly)) ||
                            (variable.hasDining && !num(variable.diningMonthly)) ||
                            (variable.hasFuel && !num(variable.fuelMonthly)) ||
                            (variable.hasMaintenance && !num(variable.maintenanceMonthly)) ||
                            (variable.hasChildcare && !num(variable.childcareMonthly)) ||
                            (variable.hasPets && (!num(variable.petFoodMonthly) && !num(variable.vetMonthly) && !num(variable.petInsuranceMonthly)))) {
                          setGuidedError('Please enter amounts for enabled variable items.');
                          return;
                        }
                        // Build review list from all steps
                        const items = [];
                        const push = (key, name, category, amount) => { if (!amount || Number.isNaN(parseFloat(amount))) return; items.push({ key, name, category, amount: parseFloat(amount) }); };
                        // Housing from step 2
                        if (household.housing === 'rent') {
                          push('rent', 'Rent', 'Housing', household.rentAmount);
                          if (household.hasRentersInsurance) push('renters_insurance', "Renter's Insurance", 'Insurance', household.rentersInsuranceMonthly);
                        } else if (household.housing === 'own') {
                          push('mortgage', 'Mortgage', 'Housing', household.mortgageAmount);
                          push('home_ins', 'Home Insurance', 'Insurance', household.homeInsuranceMonthly);
                          if (!household.propertyTaxInMortgage) push('property_tax', 'Property Tax', 'Housing', household.propertyTaxMonthly);
                          if (household.hoa) push('hoa', 'HOA', 'Housing', household.hoaMonthly);
                        }
                        // Fixed
                        if (fixed.hasElectric) push('electric', 'Electric', 'Utilities', fixed.electricMonthly);
                        if (fixed.hasGas) push('gas', 'Gas', 'Utilities', fixed.gasMonthly);
                        if (fixed.hasWater) push('water', 'Water/Trash', 'Utilities', fixed.waterMonthly);
                        if (fixed.hasInternet) push('internet', 'Internet', 'Utilities', fixed.internetMonthly);
                        if (fixed.hasMobile) push('mobile', 'Mobile', 'Utilities', fixed.mobileMonthly);
                        if (fixed.hasSubscriptions) push('subs', 'Subscriptions', 'Entertainment', fixed.subscriptionsMonthly);
                        if (fixed.hasHealthInsurance) push('health_ins', 'Health Insurance', 'Insurance', fixed.healthInsuranceMonthly);
                        if (fixed.healthOOPMonthly) push('health_oop', 'Health OOP', 'Healthcare', fixed.healthOOPMonthly);
                        if (fixed.hasAutoInsurance) push('auto_ins', 'Auto Insurance', 'Insurance', fixed.autoInsuranceMonthly);
                        // Variable
                        if (variable.hasGroceries && variable.groceriesMonthly) push('groceries', 'Groceries', 'Food', variable.groceriesMonthly);
                        if (variable.hasDining && variable.diningMonthly) push('dining', 'Dining', 'Food', variable.diningMonthly);
                        if (variable.hasFuel && variable.fuelMonthly) push('fuel', 'Fuel', 'Transportation', variable.fuelMonthly);
                        if (variable.hasMaintenance && variable.maintenanceMonthly) push('maintenance', 'Maintenance', 'Transportation', variable.maintenanceMonthly);
                        if (variable.hasChildcare) push('childcare', 'Childcare', 'Education', variable.childcareMonthly);
                        if (variable.hasPets) {
                          if (variable.petFoodMonthly) push('pet_food', 'Pet Food', 'Other', variable.petFoodMonthly);
                          if (variable.vetMonthly) push('vet', 'Veterinarian', 'Healthcare', variable.vetMonthly);
                          if (variable.petInsuranceMonthly) push('pet_ins', 'Pet Insurance', 'Insurance', variable.petInsuranceMonthly);
                        }
                        // Vehicles payments (finance/lease)
                        variable.vehicles.forEach((v, i) => {
                          if ((v.ownership === 'finance' || v.ownership === 'lease') && v.payment) {
                            push(`vehicle_${i}_payment`, 'Vehicle Payment', 'Transportation', v.payment);
                          }
                        });
                        // Compute percents of total net if available
                        const netMonthly = parseFloat(incomeForm.amount) || 0;
                        const withPct = items.map(it => ({ ...it, percent: netMonthly > 0 ? (it.amount / netMonthly) * 100 : null }));
                        setReviewItems(withPct);
                        if (withPct.length === 0) { setGuidedError('Please add at least one expense to review.'); return; }
                      }
                      setGuidedStep(prev => Math.min(prev + 1, TOTAL_WIZARD_STEPS - 1));
                      const c = document.getElementById('guided-wizard-content');
                      if (c) c.scrollTop = 0;
                    }}
                    className="px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-100"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      try {
                        setGuidedError('');
                        setGuidedLoading(true);
                        const finalItems = reviewItems
                          .map(it => ({ ...it, amount: reviewAdjustments[it.key] ?? it.amount }))
                          .filter(it => (parseFloat(it.amount) || 0) > 0);
                        if (finalItems.length === 0) { setGuidedError('Nothing to create.'); setGuidedLoading(false); return; }
                        for (const it of finalItems) {
                          try {
                            await expenseService.createExpense({
                              name: it.name,
                              amount: parseFloat(it.amount) || 0,
                              type: ['Groceries','Dining','Fuel','Maintenance'].includes(it.name) ? 'variable' : 'fixed',
                              category: it.category,
                              frequency: 'monthly',
                              dayOfMonth: 1,
                              isRecurring: true,
                              status: 'active'
                            });
                          } catch (e) {
                            // continue creating others
                          }
                        }
                        const expensesData = await expenseService.getAllExpenses(true);
                        onExpensesChange(expensesData);
                        setOpenGuidedWizard(false);
                      } catch (e) {
                        setGuidedError('Failed to create expenses.');
                      } finally {
                        setGuidedLoading(false);
                      }
                    }}
                    className="px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-100"
                  >
                    Finish
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Add Income Modal (render above wizard) */}
      {GUIDED_EXPENSE_ENABLED && addIncomeOpen && createPortal((
        <div className="fixed inset-0 bg-gray-200 bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-[10050]">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Income</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Income Name</label>
                <input type="text" value={newIncomeForm.name} onChange={(e) => setNewIncomeForm(prev => ({ ...prev, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" placeholder="Employer or source" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gross Amount</label>
                <input type="number" value={newIncomeForm.gross} onChange={(e) => setNewIncomeForm(prev => ({ ...prev, gross: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                <select value={newIncomeForm.frequency} onChange={(e) => setNewIncomeForm(prev => ({ ...prev, frequency: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900">
                  <option value="monthly">Monthly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="weekly">Weekly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State (US)</label>
                <input type="text" value={newIncomeForm.state} onChange={(e) => setNewIncomeForm(prev => ({ ...prev, state: e.target.value.toUpperCase().slice(0,2) }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900" placeholder="CA" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Net (per period)</label>
                <input type="text" value={newIncomeEstNet} readOnly className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setAddIncomeOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
              <button onClick={async () => {
                try {
                  setGuidedError('');
                  const gross = parseFloat(newIncomeForm.gross);
                  if (!newIncomeForm.name?.trim() || !gross || Number.isNaN(gross)) { setGuidedError('Please provide name and gross amount.'); return; }
                  // ensure bucket
                  let buckets = [];
                  try { buckets = await moneyBucketService.getAll(); } catch {}
                  let targetBucket = Array.isArray(buckets) ? (buckets.find(b => b?.name === 'Primary Income') || buckets.find(b => (b?.type === 'Household'))) : null;
                  if (!targetBucket) targetBucket = await moneyBucketService.create({ name: 'Primary Income', type: 'Household', description: 'Default bucket for primary income sources' });
                  const bucketId = targetBucket?._id || targetBucket?.id;
                  const est = await incomeService.estimateNet({ basePerPeriod: gross, frequency: newIncomeForm.frequency, taxProfile: { state: newIncomeForm.state || undefined } });
                  const netPerPeriod = est?.netPerPeriod || gross;
                  const payload = { name: newIncomeForm.name.trim(), amount: gross, netAmount: netPerPeriod, category: 'EMPLOYMENT', frequency: (newIncomeForm.frequency || 'monthly').toLowerCase(), bucket: bucketId, bucketId };
                  const resp = await incomeService.create(payload);
                  const created = resp?.data;
                  // refresh list and auto-select
                  const refreshed = await incomeService.getAll();
                  const list = Array.isArray(refreshed?.data) ? refreshed.data.filter(isActiveIncome) : [];
                  const enriched = list.map(inc => ({ ...inc, __grossMonthly: toMonthly(inc.amount, inc.frequency), __netMonthly: inc.netAmount ? toMonthly(inc.netAmount, inc.frequency) : null }));
                  setExistingIncomes(enriched);
                  if (created?._id) setSelectedIncomeIds(prev => Array.from(new Set([...prev, created._id])));
                  setAddIncomeOpen(false);
                } catch (e) {
                  setGuidedError(e?.response?.data?.message || 'Failed to add income.');
                }
              }} className="px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-100">Add</button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Record Payment Dialog */}
      {openPaymentDialog && (
        <div className="fixed inset-0 bg-gray-200 bg-opacity-70 backdrop-blur-sm z-50">
          <div className="absolute left-1/2 -translate-x-1/2 w-full max-w-md mx-4" style={{ top: `${payModalAnchorRef.current}px` }}>
            <div className="bg-white rounded-lg shadow-lg p-6 w-full">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              Record Payment for {selectedExpense?.name}
            </h3>
            <form onSubmit={handlePaymentSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Amount
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                      $
                    </span>
                    <input 
                      type="number"
                      name="amount"
                      value={paymentData.amount}
                      onChange={handlePaymentInputChange}
                      className="w-full pl-7 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Date
                  </label>
                  <input 
                    type="date"
                    name="date"
                    value={paymentData.date}
                    onChange={handlePaymentInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={paymentData.status}
                    onChange={handlePaymentInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                    required
                  >
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea 
                    name="notes"
                    value={paymentData.notes}
                    onChange={handlePaymentInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClosePaymentDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-100"
                >
                  Record Payment
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseSection; 